// AccountMemberWritesApi — the member WRITE transport (contract 22 §6.2 rows 11 · 13 · 14 · 15),
// the half `AccountMembersApi` deliberately left to ORP-18888. v3 issued all four straight from
// `UsersPermissions.vue` / `TransferOwnershipModal.vue` via raw `appAxios` (analysis §2.2.4).
//
// ⚠ The signatures take BRANDED ids, and that is the point. `accounts/urls.py` routes
// `users/<user_id>/remove` and `permission_invites/<pending_invite_id>/delete` — two id spaces
// that collide numerically. A `UserId` cannot be passed where an `InviteId` is required, so the
// "revoke the invite whose id happens to match a user pk, removing the wrong person" failure the
// read model was branded to prevent cannot be reintroduced at the transport either.
//
// `suppressGlobalErrorHandler`: every failure here is rendered by the pane in its own copy
// (§13/§14) — a 403 on transfer is "only the owner can do this", not a generic toast.
import type { AxiosInstance } from 'axios'

import type { InviteId, UserId } from '../models/member.types'
import type {
  BulkInvitePayload,
  InviteResultDto,
  MemberGrantPayload,
} from '../models/memberInvite.types'
import type { InvitePayload, TransferOwnershipPayload } from '../models/memberWrite.types'
import { toInviteResults } from '../service/inviteResults'

import { appHttp, unwrap } from '@/shared/http'

export class AccountMemberWritesApi {
  // Injectable for tests; defaults to the singleton instance.
  constructor(private readonly http: AxiosInstance = appHttp) {}

  private readonly cfg = { suppressGlobalErrorHandler: true } as const

  /** `POST /accounts/{a}/users/{userId}/remove` — no trailing slash (`accounts/urls.py:18`). */
  async removeMember(accountId: number | string, userId: UserId): Promise<void> {
    await this.http.post(`/accounts/${accountId}/users/${userId}/remove`, undefined, this.cfg)
  }

  /** `POST /accounts/{a}/permission_invites/{inviteId}/delete` (`accounts/urls.py:17`). */
  async revokeInvite(accountId: number | string, inviteId: InviteId): Promise<void> {
    await this.http.post(
      `/accounts/${accountId}/permission_invites/${inviteId}/delete`,
      undefined,
      this.cfg,
    )
  }

  /** `POST /accounts/{a}/users/create` — the same endpoint as an invite; a resend IS a re-invite
   *  (the view deletes the previous `PermissionInvite` for that email + scope first). */
  async sendInvite(accountId: number | string, payload: InvitePayload): Promise<void> {
    await this.http.post(`/accounts/${accountId}/users/create`, payload, this.cfg)
  }

  /**
   * `POST /accounts/{a}/users/create`, the BULK form — ORP-18889's invite modal.
   *
   * ⚠ `unwrap`, NOT `unwrapEnvelope`. This body is a BARE `{results:[…]}` with no `status` key, so
   * `unwrapEnvelope` would read `envelope.data` — `undefined` — and return it WITHOUT THROWING,
   * because its only guard is `status === 'error'`. Every invitation would then report as a
   * blank result screen with no failure anywhere. (`WorkspaceAttributeApi` uses the enveloped
   * helper because its route is enveloped; copying that choice here breaks silently.)
   *
   * Returns `null` when the body carries no `results` key at all — a legal 2xx shape the caller
   * must synthesise success for (v3 `:515-517`), and NOT the same thing as an empty array.
   */
  async sendBulkInvite(
    accountId: number | string,
    payload: BulkInvitePayload,
  ): Promise<InviteResultDto[] | null> {
    const body = unwrap(
      await this.http.post<unknown>(`/accounts/${accountId}/users/create`, payload, this.cfg),
    )
    return toInviteResults(body)
  }

  /** `POST /accounts/{a}/users/{userId}/update` for an accepted member — the body echoes the id. */
  async updateMemberGrant(
    accountId: number | string,
    userId: UserId,
    payload: MemberGrantPayload,
  ): Promise<void> {
    await this.http.post(
      `/accounts/${accountId}/users/${userId}/update`,
      { ...payload, id: userId },
      this.cfg,
    )
  }

  /**
   * The same URL for a PENDING INVITE, whose body names `pending_invite_id` instead (v3 `:495`,
   * `:551`). Two methods rather than one with a union: the branded parameter is what stops an
   * `InviteId` reaching the member form, where it would edit whichever user shares that number.
   */
  async updateInviteGrant(
    accountId: number | string,
    inviteId: InviteId,
    payload: MemberGrantPayload,
  ): Promise<void> {
    await this.http.post(
      `/accounts/${accountId}/users/${inviteId}/update`,
      { ...payload, pending_invite_id: inviteId },
      this.cfg,
    )
  }

  /** `PATCH /auth/change-ownership/{a}` — auth's URL, 22's call (contract §6.2 row 15). */
  async transferOwnership(accountId: number | string, newOwnerUserId: UserId): Promise<void> {
    const payload: TransferOwnershipPayload = { new_owner_user_id: newOwnerUserId }
    await this.http.patch(`/auth/change-ownership/${accountId}`, payload, this.cfg)
  }
}
